/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { LearnerProfile_EducationHistoryDegreeType as DegreeTypeConstant } from '__generated__/graphql-types';
import type {
  LearnerProfile_EducationHistory as EducationHistory,
  LearnerProfile_Specialization as LearnerProfileSpecialization,
} from '__generated__/graphql-types-do-not-use';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import { LONG_MONTH_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { IconButton, Link, Skeleton, Typography2 } from '@coursera/cds-core';
import { DegreeIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import {
  getEducationChoicesLearnerProfileV1,
  getMajorChoices,
  getMonths,
} from 'bundles/account-profile/common/constants';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import SkillTags from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/SkillTags';
import getCredentialsItemStyles from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/getCredentialsItemStyles';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import {
  getCredentialName,
  getViewCertificateUrl,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/credentialUtils';
import type { CredentialTypeValues } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/credentialUtils';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

export const CredentialsSectionItemPlaceholder = () => {
  return <Skeleton variant="rect" height={100} />;
};

type EducationItemProps = {
  education: EducationHistory;
  openEducationDialog: (education?: EducationHistory) => void;
};

const styles = getCredentialsItemStyles(true);

export const CredentialsSectionEducationItem = ({ education, openEducationDialog }: EducationItemProps) => {
  const monthsArray = getMonths();
  const { editable } = useContext(ProfileContext);
  const educationChoices = getEducationChoicesLearnerProfileV1();
  const majorChoicesArray = getMajorChoices();
  const {
    institutionName,
    startDate,
    graduationDate,
    degreeType,
    major,
    grade,
    gradePointAverageX100,
    isCurrentlyAStudent,
  } = education;

  const degreeTypeName = educationChoices
    .find((eduChoice) => eduChoice.name === degreeType)
    ?.value?.replace(/\(.*\)/, '');
  const majorName = majorChoicesArray.find((majorChoice) => majorChoice.name === major)?.value;

  const formatGraduationDateText = () => {
    if (!startDate && !graduationDate) return null;
    if (graduationDate) {
      if (degreeTypeName || majorName) {
        return (
          <FormattedMessage
            message={isCurrentlyAStudent ? _t('expected to graduate {month} {year}') : _t('graduated {month} {year}')}
            month={monthsArray[graduationDate.month - 1]}
            year={graduationDate.year}
          />
        );
      } else {
        return (
          <FormattedMessage
            message={isCurrentlyAStudent ? _t('Expected to graduate {month} {year}') : _t('Graduated {month} {year}')}
            month={monthsArray[graduationDate.month - 1]}
            year={graduationDate.year}
          />
        );
      }
    } else if (startDate && !graduationDate && !(degreeTypeName || majorName)) {
      // Only display the 'Started in' text if there's nothing else to show
      return (
        <FormattedMessage
          message={_t('Started in {month} {year}')}
          month={monthsArray[startDate.month - 1]}
          year={startDate.year}
        />
      );
    } else {
      return null;
    }
  };

  const formatDegreeAndMajorText = () => {
    if (!degreeTypeName && !majorName) return null;

    if (degreeTypeName && majorName) {
      return <FormattedMessage message={_t('{degreeType} in {major}')} degreeType={degreeTypeName} major={majorName} />;
    } else if (degreeTypeName) {
      return degreeTypeName.trim();
    } else if (majorName) {
      return majorName.trim();
    } else {
      return '';
    }
  };

  const formatEducationDate = () => {
    if (!startDate) return '';

    if (isCurrentlyAStudent || !graduationDate) {
      return (
        <FormattedMessage
          message={_t('{startDate} - Present')}
          startDate={`${monthsArray[startDate.month - 1]} ${startDate.year}`}
        />
      );
    } else if (graduationDate) {
      return (
        <FormattedMessage
          message={_t('{startDate} - {graduationDate}')}
          startDate={`${monthsArray[startDate.month - 1]} ${startDate.year}`}
          graduationDate={`${monthsArray[graduationDate.month - 1]} ${graduationDate.year}`}
        />
      );
    } else {
      return '';
    }
  };

  const gpa = gradePointAverageX100 && gradePointAverageX100 / 100;

  const isNoDegree = degreeType === DegreeTypeConstant.NoDegree;

  const formatGPAText = () => {
    if (!gpa || isNoDegree) return null;
    return <FormattedMessage message={_t('(CGPA {gpa})')} gpa={gpa} />;
  };

  const formatGradeText = () => {
    if (!grade || isNoDegree) return null;
    return <FormattedMessage message={_t('(Grade: {grade})')} grade={grade} />;
  };

  const degreeAndMajorText = formatDegreeAndMajorText();
  const gradutationDateText = formatGraduationDateText();

  const gradeText = formatGradeText();
  const gpaText = formatGPAText();
  return (
    <div css={styles.itemWrapper}>
      <div css={styles.iconWrapper}>
        <DegreeIcon size="large" />
      </div>
      <div css={styles.detailsWrapper}>
        {institutionName && (
          <Typography2 component="p" className="fs-mask">
            {institutionName}
          </Typography2>
        )}
        <Typography2 component="p" variant="bodyPrimary">
          {degreeAndMajorText}
          {degreeAndMajorText && gradutationDateText && ', '}
          {gradutationDateText} {gradeText || gpaText}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {formatEducationDate()}
        </Typography2>
      </div>
      {editable && (
        <IconButton
          intent="edit"
          aria-label={_t('Edit details for #{degreeTypeName} in #{majorName} at #{institutionName}.', {
            degreeTypeName,
            majorName,
            institutionName,
          })}
          size="small"
          variant="ghost"
          onClick={() => openEducationDialog(education)}
          data-testid="education-history-item-edit-icon"
          component={TrackedButton}
          requireFullyVisible={false}
          withVisibilityTracking={true}
          // not tracked in eventing v3
          trackingName="profile_edit_education_history"
        />
      )}
    </div>
  );
};

type CredentialItemProps = {
  credential: LearnerProfileSpecialization;
  type: CredentialTypeValues;
};
export const CredentialsSectionCredentialItem = ({ credential, type }: CredentialItemProps) => {
  const { retrackedEventingData, sharedEventingV3Data } = useContext(ProfileContext);
  const { productId, partners, completionDate, name, totalContentWeeks, skillNames } = credential;
  const certificateUrl = getViewCertificateUrl(credential);

  const retrackedTrack = useRetracked();
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.openEducationHistoryCertificateItem,
      linkType: 'internal',
      linkURL: certificateUrl,
    },
    product: {
      id: productId,
    },
    ...sharedEventingV3Data,
  });

  const formatCredentialDate = () => {
    if (!totalContentWeeks && !completionDate) return '';

    if (totalContentWeeks && completionDate) {
      return (
        <FormattedMessage
          message={_t('{contentLengthInMonths} month program  •  Completed {formattedCompletionDate}')}
          contentLengthInMonths={Math.ceil(totalContentWeeks / 4)}
          formattedCompletionDate={formatDateTimeDisplay(completionDate, LONG_MONTH_YEAR_DISPLAY)}
        />
      );
    }

    if (totalContentWeeks) {
      return (
        <FormattedMessage
          message={_t('{contentLengthInMonths} month program')}
          contentLengthInMonths={Math.ceil(totalContentWeeks / 4)}
        />
      );
    }

    if (completionDate) {
      return (
        <FormattedMessage
          message={_t('Completed {formattedCompletionDate}')}
          formattedCompletionDate={formatDateTimeDisplay(completionDate, LONG_MONTH_YEAR_DISPLAY)}
        />
      );
    }
    return '';
  };

  return (
    <div css={styles.itemWrapper}>
      <div css={styles.iconWrapper}>
        {/* TODO: Investigate missing partner info in some cases: https://coursera.atlassian.net/browse/LPD-477 */}
        {partners?.length ? (
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          <Imgix maxHeight={64} maxWidth={64} src={partners[0].logoUrl} alt={`${name}_logo`} />
        ) : (
          <DegreeIcon height={72} width={72} />
        )}
      </div>
      <div css={styles.detailsWrapper}>
        <Typography2 component="h3" variant="subtitleMedium">
          {getCredentialName(credential, type)}
        </Typography2>
        <SkillTags skillNames={skillNames} />
        {/* I wrap the link with a TrackedDiv for retracked visibility tracking since I cannot use tracked components on
        CDS links. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
        {TrackedLink2} on a CDS link causes the ref to refer to the TrackedLink component itself, *not* the
        underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
        throw an error since we're not referencing an html element. */}
        <TrackedDiv
          withVisibilityTracking
          requireFullyVisible={false}
          trackClicks={false}
          trackingName={EVENTS.openEducationHistoryCertificateItem}
          data={{ ...retrackedEventingData, product: { productId: credential.productId } }}
        >
          <Typography2 component="p" variant="bodySecondary">
            <Link
              ref={visibilityTrackingRef}
              href={certificateUrl}
              onClick={() => {
                retrackedTrack({
                  trackingData: { ...retrackedEventingData, product: { productId } },
                  trackingName: EVENTS.openEducationHistoryCertificateItem,
                  action: 'click',
                });

                track('click_button', {
                  button: {
                    name: EVENTS.openEducationHistoryCertificateItem,
                    linkType: 'internal',
                    linkURL: certificateUrl,
                  },
                  product: {
                    id: productId,
                  },
                  ...sharedEventingV3Data,
                });
              }}
              variant="standard"
              target="_blank"
            >
              {_t('View certificate')}
            </Link>
          </Typography2>
        </TrackedDiv>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {formatCredentialDate()}
        </Typography2>
      </div>
    </div>
  );
};
